import { useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Send, Upload } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EnquiryForm() {
  const [formData, setFormData] = useState({
    companyName: '', fullName: '', email: '', phone: '',
    length: '', breadth: '', height: '', dimensionUnit: 'MM',
    ply: '3', gsm: ['', '', ''], moq: '',
    referenceImage: '', additionalNotes: '',
    productName: 'General Enquiry', categoryName: 'General',
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const unitOptions = ['MM', 'INCHES', 'CMS'];
  const plyOptions = ['3', '5', '7', '9', '11'];

  const handlePlyChange = (value) => {
    setFormData({ ...formData, ply: value, gsm: Array(parseInt(value)).fill('') });
  };

  const handleGsmChange = (index, value) => {
    const newGsm = [...formData.gsm];
    newGsm[index] = value;
    setFormData({ ...formData, gsm: newGsm });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please upload an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(`${API}/upload-public`, fd);
      setFormData({ ...formData, referenceImage: response.data.url.startsWith('http') ? response.data.url : `${BACKEND_URL}${response.data.url}` });
      toast.success('Image uploaded!');
    } catch (error) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSubmit = async () => {
    if (!formData.companyName || !formData.fullName || !formData.email || !formData.phone) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { toast.error('Enter valid email'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API}/quote-request`, formData);
      toast.success('Enquiry submitted successfully! We will get back to you shortly.');
      setFormData({
        companyName: '', fullName: '', email: '', phone: '',
        length: '', breadth: '', height: '', dimensionUnit: 'MM',
        ply: '3', gsm: ['', '', ''], moq: '',
        referenceImage: '', additionalNotes: '',
        productName: 'General Enquiry', categoryName: 'General',
      });
    } catch (error) { toast.error('Failed to submit. Try again.'); }
    finally { setSubmitting(false); }
  };

  return (
    <section id="enquiry" className="py-28 relative overflow-hidden">
      <div className="gradient-radial-glow absolute inset-0"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <span className="badge mb-6">GET A QUOTE</span>
          <h2 className="text-4xl lg:text-6xl font-black text-foreground mb-4 tracking-tighter">Request an Enquiry</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Tell us about your packaging needs and we'll provide you with the best quote</p>
        </motion.div>

        <motion.div className="max-w-4xl mx-auto glass-card p-8 md:p-10" initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} data-testid="enquiry-form">
          <div className="space-y-5">
            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-foreground">Company Name <span className="text-destructive">*</span></Label>
                <Input value={formData.companyName} onChange={(e) => setFormData({...formData, companyName: e.target.value})} placeholder="Your company" className="bg-background/50 border-border/30 text-foreground mt-1" data-testid="enquiry-company" />
              </div>
              <div>
                <Label className="text-sm font-medium text-foreground">Full Name <span className="text-destructive">*</span></Label>
                <Input value={formData.fullName} onChange={(e) => setFormData({...formData, fullName: e.target.value})} placeholder="Your name" className="bg-background/50 border-border/30 text-foreground mt-1" data-testid="enquiry-name" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-foreground">Email <span className="text-destructive">*</span></Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="email@example.com" className="bg-background/50 border-border/30 text-foreground mt-1" data-testid="enquiry-email" />
              </div>
              <div>
                <Label className="text-sm font-medium text-foreground">Phone <span className="text-destructive">*</span></Label>
                <Input type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+91..." className="bg-background/50 border-border/30 text-foreground mt-1" data-testid="enquiry-phone" />
              </div>
            </div>

            {/* Dimensions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-medium text-foreground">Box Dimensions (optional)</Label>
                <div className="flex gap-1">
                  {unitOptions.map(unit => (
                    <button key={unit} type="button" onClick={() => setFormData({...formData, dimensionUnit: unit})}
                      className={`px-3 py-1 text-xs font-mono-spec rounded font-semibold transition-colors ${formData.dimensionUnit === unit ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-primary/20'}`}
                      data-testid={`enquiry-unit-${unit.toLowerCase()}`}
                    >{unit}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Input type="number" value={formData.length} onChange={(e) => setFormData({...formData, length: e.target.value})} placeholder="Length" className="bg-background/50 border-border/30 text-foreground font-mono-spec" data-testid="enquiry-length" />
                <Input type="number" value={formData.breadth} onChange={(e) => setFormData({...formData, breadth: e.target.value})} placeholder="Breadth" className="bg-background/50 border-border/30 text-foreground font-mono-spec" data-testid="enquiry-breadth" />
                <Input type="number" value={formData.height} onChange={(e) => setFormData({...formData, height: e.target.value})} placeholder="Height" className="bg-background/50 border-border/30 text-foreground font-mono-spec" data-testid="enquiry-height" />
              </div>
            </div>

            {/* Ply + MOQ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-foreground">Ply (optional)</Label>
                <select value={formData.ply} onChange={(e) => handlePlyChange(e.target.value)} className="w-full mt-1 px-3 py-2 border border-border/30 rounded-md bg-background/50 text-foreground focus:outline-none focus:ring-2 focus:ring-primary" data-testid="enquiry-ply">
                  {plyOptions.map(o => <option key={o} value={o}>{o} Ply</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm font-medium text-foreground">Minimum Order Quantity (optional)</Label>
                <Input type="number" value={formData.moq} onChange={(e) => setFormData({...formData, moq: e.target.value})} placeholder="e.g., 500" className="bg-background/50 border-border/30 text-foreground font-mono-spec mt-1" data-testid="enquiry-moq" />
              </div>
            </div>

            {/* Reference Image */}
            <div>
              <Label className="text-sm font-medium text-foreground mb-2 block">Reference Image (optional)</Label>
              <div className="border-2 border-dashed border-border/30 rounded-xl p-4 text-center hover:border-primary/40 transition-colors">
                <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" id="enquiry-img-upload" disabled={uploading} />
                <label htmlFor="enquiry-img-upload" className="cursor-pointer flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  {uploading ? <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div> : <Upload className="w-5 h-5" />}
                  {uploading ? 'Uploading...' : 'Click to upload an image'}
                </label>
              </div>
              {formData.referenceImage && (
                <div className="mt-2 flex items-center gap-3">
                  <img src={formData.referenceImage} alt="Ref" className="w-16 h-16 object-cover rounded-lg border border-border/30" />
                  <button onClick={() => setFormData({...formData, referenceImage: ''})} className="text-xs text-destructive hover:underline">Remove</button>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <Label className="text-sm font-medium text-foreground">Additional Notes</Label>
              <Textarea value={formData.additionalNotes} onChange={(e) => setFormData({...formData, additionalNotes: e.target.value})} placeholder="Describe your requirements, box type, printing needs, etc." rows={3} className="bg-background/50 border-border/30 text-foreground mt-1" data-testid="enquiry-notes" />
            </div>

            {/* Submit */}
            <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
              <Button onClick={handleSubmit} disabled={submitting} className="w-full py-6 text-lg font-bold rounded-xl text-primary-foreground" style={{ background: 'linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary-end)))' }} data-testid="enquiry-submit-btn">
                <Send className="h-5 w-5 mr-2" />
                {submitting ? 'Submitting...' : 'Submit Enquiry'}
              </Button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
